/**
 * EnvironmentalAtmosphere — the permanent CSS-only "intelligence
 * facility" ambience layer used across every major public page of
 * The Archive (Archive index, Oracle, Search, Topic pages…).
 *
 * Renders a stack of very-low-opacity layers inside a `position:
 * relative` ancestor to evoke a classified archive room / intelligence
 * headquarters / records vault without ever competing with content:
 *
 *   1. Volumetric ambient light spots     (radial gradients)
 *   2. Steel cabinet / shelving column    (repeating linear gradient)
 *   3. Horizontal shelf-edge hint         (repeating linear gradient)
 *   4. Blueprint measurement grid         (dual crossed linear gradients)
 *   5. Weathered paper / concrete noise   (inline SVG feTurbulence)
 *
 * Every layer is `pointer-events-none`, `aria-hidden`, and lives at
 * `-z-10` behind the content. The composite prominence sits around
 * 3–5 % — visible on inspection, invisible when reading.
 *
 * Pair with <EnvironmentalSeal /> for the seal-etched-into-the-wall
 * effect; the seal handles brand identity, this handles the room.
 *
 * variant:
 *   "vault"      — the default archive-room mood (Archive index)
 *   "operations" — cooler, more surveillance-room feel (Oracle / Search)
 *   "reading"    — warmer paper-and-lamp feel for long-form pages
 *
 * intensity: 0-1 multiplier applied to every layer's opacity.
 * Default 1.0 (already tuned to 3–5 % composite). Set 0.5 for
 * secondary sections that want an even quieter ambience.
 */
import React from "react";

const NOISE_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'>
       <filter id='n'>
         <feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/>
         <feColorMatrix type='matrix' values='0 0 0 0 0.88  0 0 0 0 0.85  0 0 0 0 0.78  0 0 0 0.5 0'/>
       </filter>
       <rect width='240' height='240' filter='url(%23n)'/>
     </svg>`.replace(/\s+/g, " ")
  );

const VARIANTS = {
  vault: {
    // Warm archive-room ambience — a single overhead cone of tungsten
    // light + a cool aisle glow from stage-right.
    lights: `
      radial-gradient(ellipse 55% 45% at 30% 0%, rgba(228,204,153,0.10) 0%, transparent 55%),
      radial-gradient(ellipse 40% 55% at 88% 100%, rgba(150,175,200,0.08) 0%, transparent 60%),
      radial-gradient(ellipse 70% 90% at 50% 55%, rgba(11,10,13,0.30) 40%, transparent 90%)
    `,
    lightsOpacity: 1.0,
    // Tall filing-cabinet / shelving column suggestion.
    cabinetsOpacity: 0.045,
    // Horizontal shelf-edge hint (files stacked in rows).
    shelvesOpacity: 0.035,
    gridOpacity: 0.035,
    noiseOpacity: 0.06,
  },
  operations: {
    // Cooler surveillance-room feel — desaturated blue-grey overhead.
    lights: `
      radial-gradient(ellipse 60% 50% at 50% 0%, rgba(150,180,210,0.10) 0%, transparent 60%),
      radial-gradient(ellipse 40% 45% at 10% 100%, rgba(217,45,32,0.06) 0%, transparent 55%),
      radial-gradient(ellipse 70% 90% at 50% 55%, rgba(11,10,13,0.32) 40%, transparent 90%)
    `,
    lightsOpacity: 1.0,
    cabinetsOpacity: 0.035,
    shelvesOpacity: 0.025,
    gridOpacity: 0.05,
    noiseOpacity: 0.05,
  },
  reading: {
    // Warmer, paper-and-lamp feel for long-form dossier reading pages.
    lights: `
      radial-gradient(ellipse 50% 40% at 50% 0%, rgba(228,204,153,0.08) 0%, transparent 55%),
      radial-gradient(ellipse 60% 85% at 50% 55%, rgba(11,10,13,0.28) 40%, transparent 90%)
    `,
    lightsOpacity: 1.0,
    cabinetsOpacity: 0.02,
    shelvesOpacity: 0.02,
    gridOpacity: 0.02,
    noiseOpacity: 0.05,
  },
};

/**
 * @param {object} props
 * @param {"vault"|"operations"|"reading"} [props.variant="vault"]
 * @param {number} [props.intensity=1]  0-1 multiplier applied to every layer.
 * @param {string} [props.className]
 * @param {string} [props.testid="environmental-atmosphere"]
 */
export const EnvironmentalAtmosphere = ({
  variant = "vault",
  intensity = 1,
  className = "",
  testid = "environmental-atmosphere",
}) => {
  const v = VARIANTS[variant] || VARIANTS.vault;
  const mul = Math.max(0, Math.min(1, intensity));

  const layerBase = "absolute inset-0 -z-10 pointer-events-none select-none";

  return (
    <>
      {/* 1. Volumetric ambient light + centre vignette */}
      <div
        aria-hidden="true"
        data-testid={testid}
        data-variant={variant}
        className={`${layerBase} ${className}`}
        style={{ background: v.lights, opacity: v.lightsOpacity * mul }}
      />

      {/* 2. Steel cabinet / filing column ribs (vertical) */}
      <div
        aria-hidden="true"
        className={layerBase}
        style={{
          opacity: v.cabinetsOpacity * mul,
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(228,223,211,0.85) 0px, rgba(228,223,211,0.85) 1px, transparent 1px, transparent 140px)",
          maskImage:
            "linear-gradient(180deg, transparent 0%, black 12%, black 88%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(180deg, transparent 0%, black 12%, black 88%, transparent 100%)",
        }}
      />

      {/* 3. Horizontal shelf-edge hint (files-stacked rows) */}
      <div
        aria-hidden="true"
        className={layerBase}
        style={{
          opacity: v.shelvesOpacity * mul,
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(228,223,211,0.6) 0px, rgba(228,223,211,0.6) 1px, transparent 1px, transparent 44px)",
          maskImage:
            "radial-gradient(ellipse 100% 80% at 50% 60%, black 20%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 100% 80% at 50% 60%, black 20%, transparent 90%)",
        }}
      />

      {/* 4. Blueprint measurement grid */}
      <div
        aria-hidden="true"
        className={layerBase}
        style={{
          opacity: v.gridOpacity * mul,
          backgroundImage: `
            linear-gradient(rgba(228,223,211,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(228,223,211,1) 1px, transparent 1px)
          `,
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 90% 100% at 50% 50%, black 25%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 90% 100% at 50% 50%, black 25%, transparent 85%)",
        }}
      />

      {/* 5. Weathered paper / concrete noise */}
      <div
        aria-hidden="true"
        className={layerBase}
        style={{
          opacity: v.noiseOpacity * mul,
          backgroundImage: `url("${NOISE_SVG}")`,
          backgroundSize: "240px 240px",
          mixBlendMode: "overlay",
        }}
      />
    </>
  );
};

export default EnvironmentalAtmosphere;
